// Deterministic pseudo-random walks for the landing-page mock charts. Points are
// generated once (fixed seeds) so the lines look organic — varied step sizes,
// multi-point runs, occasional flats and pullbacks — yet are static: identical on
// every render, no Math.random()/Date involved.

export interface WalkOpts {
  seed: number;    // fixed seed → deterministic shape
  points: number;  // number of vertices
  width: number;   // viewBox width (x spans 0..width)
  yStart: number;  // starting y (higher y = lower value, SVG coords)
  yEnd: number;    // approximate ending y (sets the overall trend)
  vol: number;     // volatility — max per-step noise amplitude
  band: number;    // how far the walk may stray from the ideal trend line
}

/** mulberry32 — tiny fast seeded RNG returning floats in [0, 1). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Build an organic-looking price walk from yStart toward yEnd. Momentum carries
 * direction across points (creating runs), random noise varies the step sizes,
 * an occasional flat/pullback breaks the rhythm, and a soft clamp keeps the walk
 * hugging the overall trend (prevents runaway and limits two walks crossing).
 */
export function seededWalk(o: WalkOpts): [number, number][] {
  const rnd = mulberry32(o.seed);
  const n = o.points;
  const drift = (o.yEnd - o.yStart) / (n - 1);
  const pts: [number, number][] = [];
  let y = o.yStart;
  let mom = 0;

  for (let i = 0; i < n; i++) {
    if (i > 0) {
      mom = mom * 0.55 + (rnd() - 0.5) * o.vol; // carry direction → multi-point runs
      y += drift + mom;
      if (rnd() < 0.15) { y -= mom * 0.9; mom *= 0.3; } // occasional flat / pullback
      // soft clamp around the ideal trend position for this x
      const ideal = o.yStart + drift * i;
      if (y < ideal - o.band) { y = ideal - o.band; mom = Math.abs(mom) * 0.4; }
      else if (y > ideal + o.band) { y = ideal + o.band; mom = -Math.abs(mom) * 0.4; }
    }
    const x = (i / (n - 1)) * o.width;
    pts.push([Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
  }
  return pts;
}

/** Points → an SVG polyline `d` string (M then L segments). */
export function toLineD(pts: [number, number][]): string {
  return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
}

/** Points → a closed area `d` string dropping to `baseY` for a gradient fill. */
export function toAreaD(pts: [number, number][], baseY: number): string {
  const firstX = pts[0][0];
  const lastX = pts[pts.length - 1][0];
  return `${toLineD(pts)} L${lastX},${baseY} L${firstX},${baseY} Z`;
}
